<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { useField, useForm } from 'vee-validate'
import * as z from 'zod'

// Zod Validation Schema
const contactSchema = toTypedSchema(
  z.object({
    name: z
      .string()
      .min(2, 'Name muss mindestens 2 Zeichen haben')
      .max(100, 'Name darf maximal 100 Zeichen haben')
      .regex(
        /^[a-zA-ZäöüÄÖÜß\s-]+$/,
        'Name darf nur Buchstaben, Leerzeichen und Bindestriche enthalten'
      ),
    email: z
      .string()
      .email('Bitte geben Sie eine gültige Email-Adresse ein'),
    subject: z.string().max(200, 'Betreff darf maximal 200 Zeichen haben').optional().or(z.literal('')),
    message: z
      .string()
      .min(20, 'Nachricht muss mindestens 20 Zeichen haben')
      .max(2000, 'Nachricht darf maximal 2000 Zeichen haben')
  })
)

// Form Setup
const { handleSubmit, isSubmitting, resetForm } = useForm({
  validationSchema: contactSchema
})

// Fields
const { value: name, errorMessage: nameError } = useField<string>('name')
const { value: email, errorMessage: emailError } = useField<string>('email')
const { value: subject, errorMessage: subjectError } = useField<string>('subject')
const { value: message, errorMessage: messageError } = useField<string>('message')

// Honeypot Field (FEAT-11)
const honeypot = ref('')

// FEAT-10: Cloudflare Turnstile Token State
const turnstileToken = ref<string>('')
const turnstileError = ref<boolean>(false)

const onTurnstileError = () => {
  turnstileToken.value = ''
  turnstileError.value = true
}

const onTurnstileExpired = () => {
  turnstileToken.value = ''
}

// n8n Webhook (FEAT-9)
const { submitContact } = useWebhook()

// UI State
const showSuccess = ref(false)
const showError = ref(false)
const errorMessage = ref('')

// Rate Limiting (FEAT-11)
const RATE_LIMIT_KEY = 'contact_form_submissions'
const RATE_LIMIT_WINDOW = 60 * 1000 // 1 minute
const MAX_SUBMISSIONS = 3

const checkRateLimit = (): boolean => {
  if (typeof window === 'undefined') return true

  const stored = localStorage.getItem(RATE_LIMIT_KEY)
  const timestamps: number[] = stored ? JSON.parse(stored) : []

  const now = Date.now()
  const recentTimestamps = timestamps.filter(t => now - t < RATE_LIMIT_WINDOW)

  return recentTimestamps.length < MAX_SUBMISSIONS
}

const recordSubmission = () => {
  if (typeof window === 'undefined') return

  const stored = localStorage.getItem(RATE_LIMIT_KEY)
  const timestamps: number[] = stored ? JSON.parse(stored) : []

  const now = Date.now()
  const recentTimestamps = timestamps.filter(t => now - t < RATE_LIMIT_WINDOW)
  recentTimestamps.push(now)

  localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(recentTimestamps))
}

// Form Submit Handler
const onSubmit = handleSubmit(async (values) => {
  // Reset previous messages
  showSuccess.value = false
  showError.value = false

  // Honeypot Check (FEAT-11)
  if (honeypot.value) {
    return
  }

  // Rate Limiting Check (FEAT-11)
  if (!checkRateLimit()) {
    showError.value = true
    errorMessage.value = 'Zu viele Anfragen. Bitte warten Sie 1 Minute.'
    return
  }

  // FEAT-10: Turnstile Token Guard
  if (!turnstileToken.value) {
    showError.value = true
    errorMessage.value = 'Bitte warten Sie, bis die Sicherheitsprüfung abgeschlossen ist.'
    return
  }

  try {
    // FEAT-9: n8n Webhook Integration mit HMAC-Signatur
    await submitContact({
      name: values.name,
      email: values.email,
      subject: values.subject || '',
      message: values.message,
      turnstile_token: turnstileToken.value, // FEAT-10
      honeypot: honeypot.value
    })

    // Record submission for rate limiting
    recordSubmission()

    // Success
    showSuccess.value = true
    turnstileToken.value = '' // FEAT-10: Token nach Submit zurücksetzen
    resetForm()

    // Auto-hide success message after 5 seconds
    setTimeout(() => {
      showSuccess.value = false
    }, 5000)
  } catch (error: any) {
    showError.value = true

    if (error.status === 403) {
      errorMessage.value = 'Sicherheitsprüfung fehlgeschlagen. Bitte laden Sie die Seite neu.'
    } else if (error.status === 401) {
      errorMessage.value = 'Authentifizierungsfehler. Bitte laden Sie die Seite neu.'
    } else if (error.status === 429) {
      errorMessage.value = 'Zu viele Anfragen. Bitte warten Sie einige Minuten.'
    } else if (error.status === 408 || error.name === 'AbortError') {
      errorMessage.value = 'Zeitüberschreitung. Bitte prüfen Sie Ihre Internetverbindung.'
    } else if (error.status >= 500) {
      errorMessage.value = 'Ein Serverfehler ist aufgetreten. Bitte versuchen Sie es später erneut oder kontaktieren Sie mich direkt per E-Mail.'
    } else {
      errorMessage.value = error.message || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.'
    }
  }
})

// SEO
useHead({
  title: 'Kontakt - Anfrage senden | Enrico Reinsdorf UX Designer',
  meta: [
    {
      name: 'description',
      content: 'Kontaktieren Sie Enrico Reinsdorf für UX Design Projekte. Freelance oder Festanstellung. Expertise in UX Design, Usability Engineering und Design Systems.'
    },
    {
      name: 'keywords',
      content: 'UX Designer Kontakt, UX Designer buchen, Freelance UX Designer Nürnberg'
    }
  ]
})
</script>

<template>
  <div class="min-h-screen bg-white">
    <!-- Hero Section -->
    <section class="bg-gray-50 border-b border-gray-200 py-16">
      <div class="max-w-4xl mx-auto px-6">
        <h1 class="text-5xl font-bold text-dark-900 mb-4">Kontakt</h1>
        <p class="text-xl text-dark-600 leading-relaxed">
          Haben Sie eine Projektidee oder Fragen? Ich freue mich auf Ihre
          Nachricht.
        </p>
      </div>
    </section>

    <!-- Contact Form Section -->
    <section class="py-16">
      <div class="max-w-2xl mx-auto px-6">
        <!-- Success Message -->
        <div
          v-if="showSuccess"
          class="mb-8 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg"
        >
          <div class="flex items-center">
            <svg
              class="w-6 h-6 text-green-500 mr-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p class="text-green-700 font-medium">
              Vielen Dank! Ihre Nachricht wurde erfolgreich gesendet.
            </p>
          </div>
        </div>

        <!-- Error Message -->
        <div
          v-if="showError"
          class="mb-8 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg"
        >
          <div class="flex items-center">
            <svg
              class="w-6 h-6 text-red-500 mr-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p class="text-red-700 font-medium">{{ errorMessage }}</p>
          </div>
        </div>

        <!-- Contact Form -->
        <form @submit="onSubmit" class="space-y-6">
          <!-- Name Field -->
          <div>
            <label for="name" class="block text-dark-700 font-medium mb-2">
              Name *
            </label>
            <input
              id="name"
              v-model="name"
              type="text"
              class="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all"
              :class="{
                'border-red-500 focus:ring-red-200': nameError,
                'border-gray-300 focus:border-primary-500 focus:ring-primary-200': !nameError
              }"
              :aria-invalid="!!nameError"
              :aria-describedby="nameError ? 'name-error' : undefined"
              placeholder="Ihr Name"
            />
            <p
              v-if="nameError"
              id="name-error"
              class="mt-2 text-sm text-red-600"
            >
              {{ nameError }}
            </p>
          </div>

          <!-- Email Field -->
          <div>
            <label for="email" class="block text-dark-700 font-medium mb-2">
              Email *
            </label>
            <input
              id="email"
              v-model="email"
              type="email"
              class="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all"
              :class="{
                'border-red-500 focus:ring-red-200': emailError,
                'border-gray-300 focus:border-primary-500 focus:ring-primary-200': !emailError
              }"
              :aria-invalid="!!emailError"
              :aria-describedby="emailError ? 'email-error' : undefined"
              placeholder="ihre.email@example.com"
            />
            <p
              v-if="emailError"
              id="email-error"
              class="mt-2 text-sm text-red-600"
            >
              {{ emailError }}
            </p>
          </div>

          <!-- Subject Field (Optional) -->
          <div>
            <label for="subject" class="block text-dark-700 font-medium mb-2">
              Betreff
            </label>
            <input
              id="subject"
              v-model="subject"
              type="text"
              class="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all"
              :class="{
                'border-red-500 focus:ring-red-200': subjectError,
                'border-gray-300 focus:border-primary-500 focus:ring-primary-200': !subjectError
              }"
              :aria-invalid="!!subjectError"
              :aria-describedby="subjectError ? 'subject-error' : undefined"
              placeholder="Worum geht es?"
            />
            <p
              v-if="subjectError"
              id="subject-error"
              class="mt-2 text-sm text-red-600"
            >
              {{ subjectError }}
            </p>
          </div>

          <!-- Message Field -->
          <div>
            <label for="message" class="block text-dark-700 font-medium mb-2">
              Nachricht *
            </label>
            <textarea
              id="message"
              v-model="message"
              rows="6"
              class="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all resize-none"
              :class="{
                'border-red-500 focus:ring-red-200': messageError,
                'border-gray-300 focus:border-primary-500 focus:ring-primary-200': !messageError
              }"
              :aria-invalid="!!messageError"
              :aria-describedby="messageError ? 'message-error' : undefined"
              placeholder="Ihre Nachricht (mindestens 20 Zeichen)"
            />
            <p
              v-if="messageError"
              id="message-error"
              class="mt-2 text-sm text-red-600"
            >
              {{ messageError }}
            </p>
          </div>

          <!-- Honeypot Field (FEAT-11) -->
          <input
            v-model="honeypot"
            type="text"
            name="website"
            tabindex="-1"
            autocomplete="off"
            style="position: absolute; left: -9999px; width: 1px; height: 1px"
            aria-hidden="true"
          />

          <!-- FEAT-10: Cloudflare Turnstile Widget -->
          <div
            class="mt-6"
            role="region"
            aria-label="Sicherheitsprüfung"
          >
            <ClientOnly>
              <Turnstile
                v-model="turnstileToken"
                :options="{
                  theme: 'light',
                  size: 'normal',
                  'error-callback': onTurnstileError,
                  'expired-callback': onTurnstileExpired
                }"
              />
            </ClientOnly>
            <p
              v-if="turnstileError"
              class="mt-2 text-sm text-red-600"
              role="alert"
            >
              Sicherheitsprüfung konnte nicht geladen werden. Bitte Seite neu laden oder
              <a
                href="mailto:enrico.reinsdorf@re-design.de"
                class="underline"
              >direkt per E-Mail kontaktieren</a>.
            </p>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="isSubmitting"
            class="w-full px-8 py-3 bg-primary-500 text-white font-medium rounded-lg transition-all hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <svg
              v-if="isSubmitting"
              class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>{{ isSubmitting ? 'Wird gesendet...' : 'Nachricht senden' }}</span>
          </button>
        </form>

        <!-- Alternative Contact Info -->
        <div class="mt-12 pt-12 border-t border-gray-200">
          <h2 class="text-2xl font-bold text-dark-900 mb-6">
            Weitere Kontaktmöglichkeiten
          </h2>
          <div class="space-y-4 text-dark-600">
            <div class="flex items-center">
              <svg
                class="w-5 h-5 text-primary-500 mr-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <a
                href="mailto:enrico.reinsdorf@re-design.de"
                class="hover:text-primary-500 transition-colors"
              >
                enrico.reinsdorf@re-design.de
              </a>
            </div>
            <div class="flex items-center">
              <svg
                class="w-5 h-5 text-primary-500 mr-3"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
                />
              </svg>
              <a
                href="https://www.linkedin.com/in/enrico-reinsdorf-6b091125/"
                target="_blank"
                rel="noopener noreferrer"
                class="hover:text-primary-500 transition-colors"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
